<template>
  <section id="products" class="products-section">
    <div class="section-header">
      <div class="header-line"></div>
      <h2 class="section-title">Our <span>Products</span></h2>
      <div class="header-line"></div>
    </div>

    <p class="section-subtitle">Comprehensive Solutions for Educational Excellence</p>

    <div class="products-container">
      <div
        class="product-card"
        v-for="(product, index) in products"
        :key="product.name"
        :class="{ 'animate': isInView }"
        :style="{ '--accent': product.accent, 'animation-delay': `${0.1 * index}s` }"
      >
        <div class="product-header">
          <div class="product-icon">
            <i :class="product.icon"></i>
          </div>
          <span class="reading-time">
            <i class="fas fa-clock"></i> 2 min read
          </span>
        </div>

        <h3 class="product-title" :aria-label="`EDUVAL ${product.name} - ${product.descriptor}`">
          <span class="product-brand">EDUVAL</span>
          <span class="product-name">{{ product.name }}</span>
          <span class="product-descriptor">{{ product.descriptor }}</span>
        </h3>

        <div class="product-description-container">
          <p class="product-description" :class="{ 'expanded': expandedDescriptions[index] }">
            {{ product.description }}
          </p>
          <div v-if="!expandedDescriptions[index]" class="fade-overlay"></div>
        </div>

        <div class="card-actions">
          <a href="#" class="action-button read-button" @click.prevent="toggleDescription(index)">
            <i class="fas fa-book-open"></i> {{ expandedDescriptions[index] ? 'Show Less' : 'Read More' }}
          </a>
          <a
            v-if="product.pdf"
            :href="getPdfUrl(product.pdf)"
            class="action-button pdf-button"
            target="_blank"
            title="View Brochure"
          >
            <i class="fas fa-file-pdf"></i> View Brochure
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'ProductsSection',
  data() {
    return {
      isInView: false,
      expandedDescriptions: [],
      products: [
        {
          name: 'Insight',
          descriptor: 'Intelligent Assessment and Accreditation Management',
          icon: 'fas fa-tasks',
          accent: '#4a6fc0',
          description: 'Streamline the accreditation process with our comprehensive system for managing evidence collection, self-studies, and continuous improvement initiatives. This platform documents assessment activities, outcomes, and improvement actions to support accreditation requirements and quality assurance efforts.',
          pdf: 'eduval_insight_brochure.pdf'
        },
        {
          name: 'Axiom',
          descriptor: 'Curriculum and Catalog Management',
          icon: 'fas fa-graduation-cap',
          accent: '#2c786c',
          description: 'Design, manage, and evaluate academic programs with ease. Our integrated curriculum management system empowers institutions to map course objectives to program outcomes and institutional goals, supporting dynamic curriculum development and continuous quality improvement. With built-in assessment capabilities, the system ensures alignment, accountability, and measurable impact. It offers comprehensive tools to manage programs, courses, electives, concentrations, and minors—all within a centralized platform. The solution also features a user-friendly catalog management module, making the creation and maintenance of academic catalogs simple, efficient, and organized.',
          pdf: 'eduval_axiom_brochure.pdf'
        },
        {
          name: 'Concord',
          descriptor: 'Integrated Syllabus Management',
          icon: 'fas fa-book',
          accent: '#6a0572',
          description: 'Standardize syllabus creation across your institution while allowing appropriate customization. Our system ensures consistency in course information, learning outcomes, assessment methods, and policies while giving instructors flexibility to adapt teaching approaches to their courses.',
          pdf: 'eduval_concord_brochure.pdf'
        },
        {
          name: 'Merit',
          descriptor: 'Faculty and Research Information Management',
          icon: 'fas fa-user-check',
          accent: '#e17055',
          description: 'A comprehensive platform that centralizes faculty data management, allowing institutions to efficiently track credentials, performance metrics, research output, and teaching activities. This system streamlines administrative processes while providing insights for faculty development and institutional advancement.',
          pdf: 'eduval_merit_brochure.pdf'
        },
        {
          name: 'Seamless',
          descriptor: 'Intelligent Timetabling and Scheduling Management',
          icon: 'fas fa-calendar-alt',
          accent: '#0a3d62',
          description: 'Efficiently manage institutional scheduling with our advanced timetabling system. This solution optimizes classroom utilization, prevents scheduling conflicts, and accommodates faculty preferences to create balanced and effective academic schedules.',
          pdf: 'eduval_seamless_brochure.pdf'
        },
        {
          name: 'Orbit',
          descriptor: 'Strategic Planning and Performance Management',
          icon: 'fas fa-chart-line',
          accent: '#ff416c',
          description: 'Transform institutional goals into measurable outcomes with our integrated planning and performance management system. This platform facilitates strategic plan development, KPI tracking, and performance analytics to ensure alignment between institutional objectives and operational execution.',
          pdf: 'eduval_orbit_brochure.pdf'
        },
        {
          name: 'Elevate',
          descriptor: 'Student Success CRM',
          icon: 'fas fa-user-graduate',
          accent: '#16a085',
          description: 'Empower students and advisors with tools for tracking academic progress, setting goals, and accessing support resources. EDUVAL Elevate provides analytics and communication features to foster student achievement and retention.',
          pdf: 'eduval_elevate_brochure.pdf'
        },
        {
          name: 'LinCard',
          descriptor: 'Digital ID',
          icon: 'fas fa-id-card',
          accent: '#c0392b',
          description: 'A secure digital identity for students and academic staff—replacing physical cards with a smart, mobile-first ID. LinCard lets members instantly exchange contact and academic information, verify identity across campus, and check in to classes and events with fast, reliable attendance tracking.'
        },
        {
          name: 'Apply',
          descriptor: 'Intelligent Admissions Management',
          icon: 'fas fa-file-signature',
          accent: '#5b4cc4',
          description: 'A modern admissions platform that carries applicants from first interest to enrolled student. Applicants browse suitable programs, review admission requirements, submit their application, upload supporting documents, and track their status in real time from any device. The admissions office works from a single queue to review and evaluate applications, verify documents, request missing information, record decisions, issue offers, and track accepted students through to enrollment. Configurable admission workflows and eligibility rules automate screening and notifications, live dashboards surface funnel volume, conversion, and processing time, and direct SIS/ERP integration moves admitted students into student records without re-entry.'
        }
      ]
    }
  },
  created() {
    // Keep the collapse state in step with the product list
    this.expandedDescriptions = Array(this.products.length).fill(false);
  },
  mounted() {
    window.addEventListener('scroll', this.checkView);
    // Initial check
    this.checkView();
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.checkView);
  },
  methods: {
    toggleDescription(index) {
      this.expandedDescriptions[index] = !this.expandedDescriptions[index];
    },
    checkView() {
      const section = document.querySelector('.products-section');
      if (!section) return;

      const position = section.getBoundingClientRect();

      // Check if section is in viewport
      if (position.top < window.innerHeight && position.bottom >= 0) {
        this.isInView = true;
      }
    },
    getPdfUrl(pdf) {
      return (process.env.BASE_URL || '/') + 'assets/Products/PDF/' + pdf;
    }
  }
}
</script>

<style scoped>
.products-section {
  padding: 0rem 2rem;
  background-color: #fcfaf2;
  position: relative;
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.header-line {
  height: 2px;
  width: 80px;
  background: linear-gradient(90deg, transparent, #d1c06e, transparent);
  margin: 0 1.5rem;
}

.section-title {
  font-size: 2.5rem;
  color: #333;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-align: center;
}

.section-title span {
  color: #d1c06e;
  position: relative;
}

.section-title span::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 100%;
  height: 3px;
  background: #d1c06e;
}

.section-subtitle {
  text-align: center;
  color: #666;
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

/* Centered flex grid: 3 per row (desktop), trailing row auto-centers */
.products-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2.5rem;
  max-width: 1400px;
  margin: 2.5rem auto;
}

.product-card {
  flex: 0 1 calc((100% - 5rem) / 3);
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 2rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(209, 192, 110, 0.1);
  opacity: 0;
  transform: translateY(30px);
  position: relative;
  overflow: hidden;
}

.product-card.animate {
  animation: slide-up 0.6s ease forwards;
}

@keyframes slide-up {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.product-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(209, 192, 110, 0.15);
}

.product-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 5px;
  background: linear-gradient(to right, var(--accent), var(--accent));
  opacity: 0.85;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.4s ease;
}

.product-card:hover::before {
  transform: scaleX(1);
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.product-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--accent);
  background-image: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px color-mix(in srgb, var(--accent) 35%, transparent);
}

.product-icon i {
  font-size: 1.5rem;
  color: white;
}

.reading-time {
  font-size: 0.85rem;
  color: #888;
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: rgba(209, 192, 110, 0.1);
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
}

.reading-time i {
  color: #d1c06e;
}

/* Two-tier branded title */
.product-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 1rem;
  margin-left: -15px;
  padding: 10px 15px;
  border-radius: 0 20px 20px 0;
  background: color-mix(in srgb, var(--accent) 8%, transparent);
  box-shadow: inset 5px 0 0 var(--accent);
  min-height: 6.5rem;
}

.product-brand {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: #d1c06e;
}

.product-name {
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.2;
  color: var(--accent);
}

.product-descriptor {
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.35;
  color: #444;
}

.product-description-container {
  position: relative;
  flex: 1 1 auto;
  margin-bottom: 1.25rem;
}

.product-description {
  color: #666;
  line-height: 1.6;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  transition: all 0.3s ease;
}

.product-description.expanded {
  -webkit-line-clamp: unset;
  display: block;
}

.fade-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 30px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
  pointer-events: none;
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: auto;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 0.7rem 1.1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.action-button i {
  font-size: 1.1rem;
}

.read-button {
  flex: 1 1 auto;
  background-color: rgba(209, 192, 110, 0.12);
  color: #b79a2f;
}

.read-button:hover {
  background-color: rgba(209, 192, 110, 0.22);
  color: #a3841f;
}

.pdf-button {
  flex: 1 1 auto;
  background-color: white;
  color: #444;
  border: 1px solid #eee;
}

.pdf-button:hover {
  background-color: #d1c06e;
  border-color: #d1c06e;
  color: white;
}

@media (max-width: 1200px) {
  .product-card {
    flex: 0 1 calc((100% - 2.5rem) / 2);
  }
}

@media (max-width: 768px) {
  .products-section {
    padding: 5rem 1.5rem;
  }

  .products-container {
    gap: 1.5rem;
    margin: 2rem auto;
  }

  .product-card {
    flex: 1 1 100%;
  }

  .section-title {
    font-size: 2rem;
  }

  .product-title {
    min-height: auto;
  }

  .header-line {
    width: 50px;
  }
}
</style>
